import { type DateValue, getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { useMemo } from "react";
import { create } from "zustand";
import { useDateLimits } from "@/api/date-limits";

const start = today(getLocalTimeZone());
const end = start.subtract({ days: 7 });

export const calculateDateRange = (
  storedRange: { start: DateValue; end: DateValue } | null | undefined,
  limits: { minDate: DateValue; maxDate: DateValue } | undefined,
): { start: DateValue; end: DateValue } | null => {
  if (storedRange === undefined && limits === undefined) return null;

  if (!storedRange) {
    // Determine the end date: earlier of the module's end or limits maxDate
    const effectiveEnd = limits && limits.maxDate < end ? limits.maxDate : end;

    // Determine the start date: 7 days before end, or limits minDate (whichever is later)
    const sevenDaysBeforeEnd = effectiveEnd.subtract({ days: 7 });
    const effectiveStart = limits && limits.minDate > sevenDaysBeforeEnd ? limits.minDate : sevenDaysBeforeEnd;

    return { start: effectiveStart, end: effectiveEnd };
  }

  return {
    start: storedRange.start,
    end: storedRange.end,
  };
};

const useDateStore = create<{
  dateRange: { start: DateValue; end: DateValue } | null;
  setDateRange: (range: { start: DateValue; end: DateValue } | null) => void;
}>((set) => ({
  dateRange: null,
  setDateRange: (range) => set(() => ({ dateRange: range })),
}));

export const useExplorerDateFilter = () => {
  const { data: dateLimits } = useDateLimits();
  const storedRange = useDateStore((state) => state.dateRange);
  const setStoredRange = useDateStore((state) => state.setDateRange);

  const limits = useMemo(() => {
    if (!dateLimits) return undefined;
    return {
      minDate: parseDate(dateLimits.minDate),
      maxDate: parseDate(dateLimits.maxDate),
    };
  }, [dateLimits]);

  const dateRange: { start: DateValue; end: DateValue } | null = useMemo(() => {
    return calculateDateRange(storedRange, limits);
  }, [storedRange, limits]);

  const changeDate = (range: { start: DateValue; end: DateValue } | null) => {
    if (range) setStoredRange(range);
    else setStoredRange(null);
  };

  const clear = () => {
    setStoredRange(null);
  };

  return { dateRange, isApplied: !!storedRange, changeDate, clear, limits };
};
